// SessionManager.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PlusIcon, 
  ChatBubbleLeftRightIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

const SessionManager = ({ 
  sessions, 
  currentSessionId, 
  onSessionSelect, 
  onSessionCreate, 
  onSessionDelete,
  onSessionUpdate 
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const handleCreateSession = async () => {
    setIsCreating(true);
    try {
      const response = await apiClient.post('/sessions');
      onSessionCreate(response.data.session);
      toast.success('새 대화가 시작되었습니다!');
    } catch (error) {
      console.error('세션 생성 오류:', error);
      toast.error('새 대화 생성에 실패했습니다.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('이 대화를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await apiClient.delete(`/sessions/${sessionId}`);
      onSessionDelete(sessionId);
      toast.success('대화가 삭제되었습니다.');
    } catch (error) {
      console.error('세션 삭제 오류:', error);
      toast.error('대화 삭제에 실패했습니다.');
    }
  };

  const handleEditStart = (session, e) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditTitle(session.title);
  };

  const handleEditSave = async (sessionId) => {
    if (!editTitle.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }

    try {
      await apiClient.patch(`/sessions/${sessionId}`, { title: editTitle });
      onSessionUpdate(sessionId, { title: editTitle });
      setEditingSessionId(null);
      toast.success('제목이 수정되었습니다.');
    } catch (error) {
      console.error('세션 수정 오류:', error);
      toast.error('제목 수정에 실패했습니다.');
    }
  };

  const handleEditCancel = () => {
    setEditingSessionId(null);
    setEditTitle('');
  };

  return (
    <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* 헤더 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2" />
            대화 목록
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleCreateSession}
            disabled={isCreating}
            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      {/* 세션 목록 */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence>
          {sessions.map((session) => (
            <motion.div
              key={session.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                currentSessionId === session.id ? 'bg-blue-50 border-blue-200' : ''
              }`}
              onClick={() => onSessionSelect(session.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {editingSessionId === session.id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleEditSave(session.id);
                          } else if (e.key === 'Escape') {
                            handleEditCancel();
                          }
                        }}
                      />
                      <button
                        onClick={() => handleEditSave(session.id)}
                        className="p-1 text-green-600 hover:text-green-800"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-medium text-gray-800 truncate">
                        {session.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {session.messageCount}개 메시지
                      </p>
                      <p className="text-xs text-gray-400">
                        {session.updatedAt ? 
                          new Date(session.updatedAt).toLocaleDateString('ko-KR', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '방금 전'
                        }
                      </p>
                    </>
                  )}
                </div>
                
                {editingSessionId !== session.id && (
                  <div className="flex items-center space-x-1 ml-2">
                    <button
                      onClick={(e) => handleEditStart(session, e)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <PencilIcon className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteSession(session.id, e)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {sessions.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>아직 대화가 없습니다.</p>
            <p className="text-sm">새 대화를 시작해보세요!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionManager;
